import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, Link } from 'react-router-dom';
import * as yup from 'yup';
import { RootState } from '@@/store';
import { useLoginMutation } from '@@/services/auth';
import { Roles } from '@@/constants/user';
import StandardInput from '@@/components/Inputs/StandardInput';
import { LogIn, Mail, Lock, Loader2, FileText } from 'lucide-react';

const schema = yup
  .object({
    username: yup
      .string()
      .email('Veuillez entrer un identifiant valide')
      .required('Veuillez entrer votre identifiant'),
    password: yup.string().required('Veuillez entrer votre mot de passe'),
    role: yup.string().oneOf(Object.values(Roles)),
  })
  .required();

const SigninPage = () => {
  const auth = useSelector((state: RootState) => state.auth);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Alacaza.SigninInput>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const navigate = useNavigate();
  const [login, { isSuccess }] = useLoginMutation();

  const loginHandler = useCallback(
    async (data: Alacaza.SigninInput) => {
      return login({ ...data, scope: Roles.PARTNER });
    },
    [login],
  );

  useEffect(() => {
    if (isSuccess) {
      reset();
      navigate('/home');
    }
  }, [isSuccess, navigate, reset]);

  useEffect(() => {
    if (auth.accessToken) {
      navigate('/home');
    }
  }, [auth.accessToken, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100 flex flex-col justify-center items-center px-4 py-8">
      {/* Logo */}
      <div className="mb-4">
        <img
          src="/logo.svg"
          className="w-32 sm:w-40 h-auto"
          alt="Alacaza Pro"
        />
      </div>

      {/* Carte de connexion */}
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-blue-600 px-6 py-5 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-xl mb-2 backdrop-blur-sm">
              <LogIn size={28} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
              Accès Partenaire
            </h1>
            <p className="text-primary-100 text-sm">
              Connectez-vous à votre espace professionnel
            </p>
          </div>

          {/* Formulaire */}
          <div className="px-6 py-5">
            <form autoComplete="off" className="space-y-4">
              {/* Email/Identifiant */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Mail size={16} className="text-primary-600" />
                  <label className="text-sm font-medium text-neutral-700">
                    Identifiant *
                  </label>
                </div>
                <StandardInput
                  register={register}
                  id="username"
                  label=""
                  type="text"
                  placeholder="votre@email.com"
                  error={errors.username}
                />
              </div>

              {/* Mot de passe */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Lock size={16} className="text-primary-600" />
                    <label className="text-sm font-medium text-neutral-700">
                      Mot de passe *
                    </label>
                  </div>
                  <Link
                    to="/reset-password"
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <StandardInput
                  register={register}
                  id="password"
                  label=""
                  type="password"
                  placeholder="••••••••"
                  error={errors.password}
                />
              </div>

              {/* Bouton de connexion */}
              <button
                type="button"
                className={`
                  w-full py-3 px-4 rounded-lg font-semibold text-white text-base
                  transition-all duration-200 flex items-center justify-center space-x-2
                  ${
                    isSubmitting
                      ? 'bg-neutral-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  }
                `}
                onClick={handleSubmit(loginHandler)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Connexion...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Se connecter</span>
                  </>
                )}
              </button>
            </form>

            {/* CGU */}
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <div className="flex items-start space-x-2 text-xs text-neutral-600">
                <FileText
                  size={14}
                  className="text-neutral-400 flex-shrink-0 mt-0.5"
                />
                <p className="leading-relaxed">
                  En vous connectant, vous acceptez{' '}
                  <a
                    href="https://static.alacaza.fr/cgu-pro.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 font-medium underline transition-colors"
                  >
                    les conditions générales d'utilisation
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SigninPage;
